import { useQuery } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";

/**
 * The Capacity page's headline cards -- `GET /card-calculations`, built server-side from
 * athena_bfsi_prc.card_calculations (sql/68_). The card arithmetic lives there, once; the browser only
 * formats. The bases are fixed by the card spec -- net investment for the portfolio, DCB for every
 * outstanding figure -- so the "Measured in" dropdown does not move these cards.
 *
 * `null` always means "unmeasured" or "not set", never zero: a DCB that is not populated, or a ceiling
 * that is expected but missing.
 */
export interface FundingMix {
  /** Net investment of the debt facilities (CAP 1 + CAP 2) / Total portfolio, percent. */
  debtFundedPct: number | null;
  /** 100 - debtFundedPct. */
  equityFundedPct: number | null;
  debtOutstanding: number | null;
  /** total_ceiling of the debt facilities - their net investment. Signed. */
  remainingDebtCapacity: number | null;
  equityCeiling: number | null;
  equityOutstanding: number | null;
  /** total_ceiling of the Equity facility - its net investment. Signed. */
  remainingEquityCapacity: number | null;
}

export interface CapacityCardFigures {
  /** total_ceiling (debt + equity) of the investor + facility row; the Loans / Leases dropdown does not move it. */
  committedCeiling: number | null;
  /** Net investment for ALL the dropdowns. */
  totalPortfolio: number | null;
  /** DCB for all the dropdowns; the % is of the committed ceiling. */
  totalOutstanding: number | null;
  totalOutstandingPct: number | null;
  /** DCB with the product replaced by Loans / Leases. */
  loansOutstanding: number | null;
  loansOutstandingPct: number | null;
  leasesOutstanding: number | null;
  leasesOutstandingPct: number | null;
  /** committedCeiling - totalOutstanding. Signed. */
  remainingCapacity: number | null;
  /** 100 - totalOutstandingPct. */
  remainingCapacityPct: number | null;
  /** Only on "All facilities". */
  funding: FundingMix | null;
}

export interface CardCalculationsResponse {
  requestedAsOf: string;
  /** The file date actually read: the latest one at or before the requested date. */
  asOfDate: string | null;
  facility: string;
  investorName: string;
  /** "Loans", "Leases" or "Loans & Leases". */
  product: string;
  available: boolean;
  note?: string;
  cards?: CapacityCardFigures;
}

export function useCardCalculations(
  facility: string,
  asOfDate: string,
  investor: string,
  product: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["prc-card-calculations", facility, asOfDate, investor, product],
    queryFn: () => {
      const params = new URLSearchParams({ facility, as_of: asOfDate, investor, product });
      return getJson<CardCalculationsResponse>(`${PRC_PREFIX}card-calculations?${params.toString()}`);
    },
    enabled,
  });
}
